import React from 'react';
import { Home, DollarSign, PieChart, TrendingUp, Brain, Target, Lightbulb, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { MortgageData } from '@/types/calculator';

interface CalculatorReportEmailPreviewProps {
  mortgageData: MortgageData;
  hasBuydownData?: boolean;
  hasSupremeIntelligence?: boolean;
}

export const CalculatorReportEmailPreview: React.FC<CalculatorReportEmailPreviewProps> = ({
  mortgageData,
  hasBuydownData = false,
  hasSupremeIntelligence = false,
}) => {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);

  const formatPercent = (value: number) => `${value.toFixed(2)}%`;

  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const cashToClose = mortgageData.downPayment + mortgageData.closingCosts.total;
  const ltvRatio = (loanAmount / mortgageData.homePrice) * 100;
  const riskLevel = ltvRatio > 95 ? 'High' : ltvRatio > 80 ? 'Medium' : 'Low';

  const formatLoanType = (type: string) => {
    const types: Record<string, string> = {
      conventional: 'Conventional',
      fha: 'FHA',
      va: 'VA',
      usda: 'USDA',
    };
    return types[type] || type.toUpperCase();
  };

  return (
    <div className="space-y-4 text-sm">
      {/* Mortgage Summary */}
      <Card className="border-border/50">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Home className="h-4 w-4 text-primary" />
            Mortgage Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4 space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Home Price</span>
            <span className="font-medium">{formatCurrency(mortgageData.homePrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Down Payment</span>
            <span className="font-medium">
              {formatCurrency(mortgageData.downPayment)} ({mortgageData.downPaymentPercentage}%)
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Loan Amount</span>
            <span className="font-medium">{formatCurrency(loanAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Interest Rate</span>
            <span className="font-medium">{formatPercent(mortgageData.interestRate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Loan Term</span>
            <span className="font-medium">{mortgageData.loanTerm} years</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Loan Type</span>
            <span className="font-medium">{formatLoanType(mortgageData.loanType)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Payment */}
      <Card className="border-border/50">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            Monthly Payment
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <div className="bg-emerald-500/10 rounded-lg p-3 mb-3 text-center">
            <p className="text-xs text-muted-foreground">Total Monthly</p>
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(mortgageData.monthlyPayment.total)}
            </p>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Principal & Interest</span>
              <span>
                {formatCurrency(
                  mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Taxes</span>
              <span>{formatCurrency(mortgageData.monthlyPayment.taxes)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Insurance</span>
              <span>{formatCurrency(mortgageData.monthlyPayment.insurance)}</span>
            </div>
            {mortgageData.monthlyPayment.pmi > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">PMI</span>
                <span>{formatCurrency(mortgageData.monthlyPayment.pmi)}</span>
              </div>
            )}
            {mortgageData.monthlyPayment.hoa > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">HOA</span>
                <span>{formatCurrency(mortgageData.monthlyPayment.hoa)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Closing Costs */}
      <Card className="border-border/50">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChart className="h-4 w-4 text-amber-500" />
            Closing Costs
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-amber-500/10 rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Closing Costs</p>
              <p className="font-semibold text-amber-600">
                {formatCurrency(mortgageData.closingCosts.total)}
              </p>
            </div>
            <div className="bg-blue-500/10 rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Cash to Close</p>
              <p className="font-semibold text-blue-600">{formatCurrency(cashToClose)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics */}
      <Card className="border-border/50">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-500" />
            Risk Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Risk Level</p>
              <p
                className={`font-semibold ${
                  riskLevel === 'Low'
                    ? 'text-emerald-600'
                    : riskLevel === 'Medium'
                    ? 'text-amber-600'
                    : 'text-red-600'
                }`}
              >
                {riskLevel}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">LTV Ratio</p>
              <p className="font-semibold">{formatPercent(ltvRatio)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Buydown Analysis Preview */}
      {hasBuydownData && (
        <Card className="border-border/50">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-500" />
              Buydown Analysis
              <Badge variant="secondary" className="text-xs">Included</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4">
            <p className="text-xs text-muted-foreground">
              Scenario comparison, builder incentive impact, and optimal strategy recommendations
            </p>
          </CardContent>
        </Card>
      )}

      {/* Long-Term Projections Preview */}
      <Card className="border-border/50">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-indigo-500" />
            Long-Term Projections
            <Badge variant="secondary" className="text-xs">Included</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <p className="text-xs text-muted-foreground">
            5, 10, 15, 20, 30-year milestones with equity growth and net worth impact
          </p>
        </CardContent>
      </Card>

      {/* Optimization Opportunities Preview */}
      <Card className="border-border/50">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-yellow-500" />
            Optimization Opportunities
            <Badge variant="secondary" className="text-xs">Included</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <p className="text-xs text-muted-foreground">
            Prioritized savings opportunities with action items
          </p>
        </CardContent>
      </Card>

      {/* Supreme Intelligence */}
      {hasSupremeIntelligence && (
        <Card className="border-border/50">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Brain className="h-4 w-4 text-purple-500" />
              Supreme Intelligence
              <Badge variant="secondary" className="text-xs">Full Report</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4 space-y-2">
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Loan program comparison & recommendations</p>
              <p>• Financial health score with component breakdown</p>
              <p>• Personalized action items & improvement roadmap</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
